<script>
  import { onMount, createEventDispatcher } from "svelte"
  import { loadJs } from "../Script/loadLibrary"
  import notify from "../Script/notify"
  const dispatch = createEventDispatcher()

  /**
   * @type {String} - file's object uri
   */
  export let uri = ""

  /**
   * @type {String} -  whether to show or note pdf close button
   */
  export let showClearBtn = true

  export let pageDimensions = []
  /**
   * @type {HTMLElement} - canvas element for load pdf
   */
  let canvas = null

  /**
   * @type {Object} - store the pdf details
   */
  let _PDFDOC

  /**
   * @type {Boolean} - whether to show or note loading while canvas process for showing pdf
   */
  let loadingPDF = false

  /**
   * @type {Number} - PDF total page number
   */
  let totalPages = 1

  /**
   * @type {Number} - PDF current page number
   */
  let currentpage = 1

  /**
   * @type {Number} - while changing pages number simultaneously that pages number stored as pending page number
   */
  let pageNumPending = null
  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {closeNotification}
   */
  let closeNotification = {
    close() {},
  }

  /**
   * trigger showPDF Function
   */
  onMount(async () => {
    try {
      await showPDF(uri)
    } catch (error) {
      console.error(error)
      if (error?.name == "PasswordException") {
        error = "Password Protected Files not supported"
        dispatch("click")
      }
      closeNotification.close()
      const info = "PAGE: Global (PDF Preview)\nEVENT: onMount"
      closeNotification = notify.danger(error, info)
    }
  })

  /**
   * Loads and displays a PDF from the given URL using pdf.js.
   *
   * @param {string} url - The URL of the PDF to display.
   * @returns {Promise<void>} - A promise that resolves when the PDF is successfully loaded and displayed.
   */
  const showPDF = async (url) => {
    // Set the loading flag to true to indicate that the PDF is loading
    loadingPDF = true
    // Hide the signature image if it's visible
    // if (signImg) signImg = false

    // Load the pdf.js library if it's not already loaded
    await loadJs("pdfjsLib", "assets/lib/pdf.js")

    // Load the pdf.js worker if it's not already loaded
    await loadJs("pdfjsWorker", "assets/lib/pdf.worker.js")

    // Specify the workerSrc property for pdf.js
    pdfjsLib.GlobalWorkerOptions.workerSrc = "assets/lib/pdf.worker.js"

    // Start downloading the PDF asynchronously
    let loadingTask = pdfjsLib.getDocument(url)

    // Await the completion of the PDF loading task
    loadingTask = loadingTask.promise
    _PDFDOC = await loadingTask

    totalPages = _PDFDOC.numPages

    let hasRotation = false

    pageDimensions = []

    for (let i = 0; i < totalPages; i++) {
      const page = await _PDFDOC.getPage(i + 1)
      const { width, height } = page.getViewport({ scale: 1 })

      pageDimensions.push(`${width}*${height}`)

      if (!hasRotation && page._pageInfo.rotate !== 0) hasRotation = true
    }

    if (hasRotation) {
      const info = "PAGE: Global (PDF Preview)\nEVENT: While validating PDF page rotation"
      notify.danger("Some pages in the uploaded document are rotated. Such documents are not allowed, as the rotation may cause issues with signature placement.", info)
      dispatch("click")
      return
    }

    // Get the total number of pages in the PDF document
    if (canvas) canvas.scrollIntoView()

    // Display the current page of the PDF
    showPage(currentpage)
  }

  /**
   * Renders a specific page of the loaded PDF document onto a canvas.
   *
   * @param {number} pageno - The page number to display.
   * @returns {Promise<void>} - A promise that resolves when the page is successfully rendered.
   */
  const showPage = async (pageno) => {
    try {
      // Set the loading flag to true to indicate that a PDF page is being loaded
      loadingPDF = true

      // Get the specified page from the PDF document
      let page = await _PDFDOC.getPage(pageno)

      // Get the viewport for the page at a scale of 1
      let viewport = page.getViewport({ scale: 1 })

      // Prepare the canvas using the PDF page dimensions
      let context = canvas.getContext("2d")
      canvas.height = viewport.height
      canvas.width = viewport.width

      // Create the render context with canvas context and viewport
      let renderContext = {
        canvasContext: context,
        viewport: viewport,
      }

      // Render the PDF page into the canvas context
      await page.render(renderContext).promise

      // Set the loading flag to false to indicate that the PDF page has finished loading
      loadingPDF = false

      // Check if a new page rendering is pending
      if (pageNumPending !== null) {
        // Render the pending page and reset the pending page number
        showPage(pageNumPending)
        pageNumPending = null
      }
    } catch (error) {
      // Set the loading flag to true in case of an error (could be false as well to stop loading indicator)
      loadingPDF = true
      // Log the error (commented out)
    }
  }

  /**
   * Queues a page to be rendered if a PDF page is currently being loaded.
   * If no page is being loaded, it immediately renders the specified page.
   *
   * @param {number} pageno - The page number to render.
   */
  const queueRenderPage = (pageno) => {
    // Check if a PDF page is currently being loaded
    if (loadingPDF) {
      // Set the pending page number to the specified page number
      pageNumPending = pageno
    } else {
      // Render the specified page immediately
      showPage(pageno)
    }
  }

  /**
   * Changes the current page of the PDF based on user input and renders the new page.
   *
   * @param {Event} e - The event object from the input field.
   */
  const changePdfPageNo = (e) => {
    // Check if the entered page number is less than the total number of pages and not zero
    if (e.target.value <= totalPages && e.target.value != 0) {
      // Update the current page number with the entered value
      currentpage = Number(e.target.value)
      // Queue the rendering of the new page
      queueRenderPage(currentpage)
    } else {
      // If the entered page number exceeds the total number of pages
      if (e.target.value > totalPages) {
        let prevPage = currentpage

        // Set the current page number to the last page
        currentpage = totalPages

        // Queue the rendering of the last page
        if (prevPage != currentpage) queueRenderPage(totalPages)
      } else if (e.target.value < 1) {
        // If the entered page number is less than 1
        // Clear the current page number input
        currentpage = ""
        // Show the first page of the PDF
        showPage(1)
      }
    }
  }

  /**
   * Changes the current page of the PDF based on scroll input and renders the new page.
   *
   * @param {WheelEvent} e - The wheel event object.
   */
  const changePdfPageNoWhileScroll = (e) => {
    // If the scroll direction is up and the current page is less than the total pages
    if (e.deltaY < 0 && currentpage > 1) {
      // Increment the current page number
      currentpage--
      // Queue the rendering of the new page
      queueRenderPage(currentpage)
    }
    // If the scroll direction is down and the current page is greater than 1
    else if (e.deltaY > 0 && currentpage < totalPages) {
      // Decrement the current page number
      currentpage++
      // Queue the rendering of the new page
      queueRenderPage(currentpage)
    }
  }
</script>

<div class="space-y-3">
  <!-- canvas section -->
  <div class="relative mx-auto flex w-full flex-col overflow-hidden rounded-md mobile:w-[412px] {loadingPDF ? 'min-h-[583px] animate-pulse  bg-gray-100/20' : ''} items-center justify-center gap-3">
    {#if showClearBtn}
      <button on:click class="absolute right-6 top-6 text-xl text-gray-500 hover:text-red-600 active:text-red-400">
        <i class="fa-solid fa-circle-xmark" />
      </button>
    {/if}
    <canvas on:wheel|preventDefault|stopPropagation={changePdfPageNoWhileScroll} bind:this={canvas} class="h-full w-full rounded-md {loadingPDF ? '' : 'border border-gray-300'}" />
  </div>

  <!-- pagination -->
  <div class="flex w-full items-center justify-center gap-3 md:mt-3">
    <!-- Previous button -->
    <button
      on:click={() => {
        currentpage--
        queueRenderPage(currentpage)
      }}
      type="button"
      disabled={currentpage == 1}
      class="flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-800 text-xs text-gray-800 hover:border-black hover:text-black active:border-white active:bg-blue-500 active:text-white disabled:cursor-not-allowed dark:hover:border-white dark:hover:text-white md:h-8 md:w-8 md:text-base"
    >
      <i class="fa-solid fa-chevron-left" />
    </button>

    <!-- Page Number Input Box -->
    <div class="flex h-8 w-auto items-center justify-between px-2">
      <input
        min="1"
        max={totalPages}
        bind:value={currentpage}
        type="number"
        disabled={totalPages == 1}
        class="w-auto rounded-md border border-none bg-transparent text-left font-mono font-bold text-black focus:outline-none dark:text-white"
        on:input|stopPropagation={changePdfPageNo}
        on:paste|preventDefault
        on:wheel|preventDefault|stopPropagation={changePdfPageNoWhileScroll}
        on:blur={() => {
          if (currentpage == "") currentpage = 1
        }}
      />
      <!-- Total Page Number -->
      <p class="select-none text-center font-mono font-bold text-gray-800">
        /&nbsp;{totalPages}
      </p>
    </div>

    <!-- Next Page Button -->
    <button
      on:click={() => {
        currentpage++
        queueRenderPage(currentpage)
      }}
      type="button"
      disabled={currentpage == totalPages}
      class="flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-800 text-xs text-gray-800 hover:border-black hover:text-black active:border-white active:bg-blue-500 active:text-white disabled:cursor-not-allowed dark:hover:border-white dark:hover:text-white md:h-8 md:w-8 md:text-base"
    >
      <i class="fa-solid fa-chevron-right" />
    </button>
  </div>
</div>
